import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FaPalette, FaCheckCircle, FaSpinner, FaCheck, FaMagic, FaExclamationTriangle, FaExternalLinkAlt, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { eventThemeApi } from '../../api/themes/eventThemeApi';
import { useAuthContext } from '../../context/AuthContext';
import './styles/Website-Templates.css';

const WebsiteTemplates = () => {
    const { currentEvent } = useAuthContext();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeWebsiteId, setActiveWebsiteId] = useState(null);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [selectedTemplateForModal, setSelectedTemplateForModal] = useState(null);
    const [colorSettings, setColorSettings] = useState({
        primary_color: '#4169e1',
        secondary_color: '#1e293b',
        tertially_color: '#f8fafc'
    });
    const [savingTheme, setSavingTheme] = useState(false);

    const fetchCustomWebsites = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch custom website options from GET /api/event/custom-websites
            const res = await eventThemeApi.getCustomWebsites();
            const rawList = res?.data?.data?.data || res?.data?.data || res?.data?.custom_websites || res?.data || res || [];

            if (Array.isArray(rawList)) {
                const formatted = rawList.map((item, idx) => {
                    const isSelected = item.is_selected === true || item.is_selected === 1 || String(item.id) === String(currentEvent?.custom_website_id);
                    return {
                        id: item.id || idx + 1,
                        name: item.name || item.title || item.template_name || `Custom Website #${item.id || idx + 1}`,
                        category: item.category || (item.code ? item.code.toUpperCase() : null) || item.theme_category || 'Website Template',
                        description: item.description || item.details || '',
                        image: item.design_path || item.preview_path || item.image || item.thumbnail || item.preview_image || null,
                        preview_url: item.preview_url || item.url || item.demo_url || null,
                        is_selected: Boolean(isSelected),
                        status: item.status
                    };
                });

                // Sort so that the selected template is always first ("is selected should be up")
                formatted.sort((a, b) => (b.is_selected ? 1 : 0) - (a.is_selected ? 1 : 0));

                setTemplates(formatted);

                // Set active website ID if selected item present or in currentEvent
                const selectedItem = formatted.find(item => item.is_selected);
                if (selectedItem) {
                    setActiveWebsiteId(selectedItem.id);
                } else if (currentEvent?.custom_website_id) {
                    setActiveWebsiteId(currentEvent.custom_website_id);
                }
            } else {
                setTemplates([]);
            }
        } catch (err) {
            console.error("Error loading custom websites from API:", err);
            setError("Imeshindwa kupakia custom website options kutoka kwenye API.");
        } finally {
            setLoading(false);
        }
    }, [currentEvent?.custom_website_id]);

    useEffect(() => {
        fetchCustomWebsites();
    }, [fetchCustomWebsites]);

    // Open modal to select website template and specify colors
    const handleOpenSelectModal = (template) => {
        setSelectedTemplateForModal(template);
        setShowThemeModal(true);
    };

    // Save custom website selection and theme colors via /api/event/theme-settings
    const handleSaveThemeSettings = async (e) => {
        if (e) e.preventDefault();
        if (!selectedTemplateForModal) return;

        if (!colorSettings.primary_color || !colorSettings.secondary_color || !colorSettings.tertially_color) {
            toast.warning("Tafadhali jaza rangi zote tatu zinazohitajika.");
            return;
        }

        setSavingTheme(true);
        try {
            await eventThemeApi.saveThemeSettings({
                custom_website_id: selectedTemplateForModal.id,
                primary_color: colorSettings.primary_color,
                secondary_color: colorSettings.secondary_color,
                tertially_color: colorSettings.tertially_color
            });

            setActiveWebsiteId(selectedTemplateForModal.id);
            setTemplates(prev => {
                const updated = prev.map(t => ({
                    ...t,
                    is_selected: t.id === selectedTemplateForModal.id
                }));
                // Keep selected at the top
                return [...updated].sort((a, b) => (b.is_selected ? 1 : 0) - (a.is_selected ? 1 : 0));
            });

            toast.success(`Tovuti ya "${selectedTemplateForModal.name}" na rangi zake zimehifadhiwa kikamilifu!`);
            setShowThemeModal(false);
            setSelectedTemplateForModal(null);
        } catch (err) {
            console.error("Error saving website template & theme settings:", err);
            const errMsg = err?.response?.data?.message || "Imeshindwa kuhifadhi muundo na rangi za tovuti. Tafadhali jaribu tena.";
            toast.error(errMsg);
        } finally {
            setSavingTheme(false);
        }
    };

    return (
        <Container fluid className="templates-container py-4 px-2 px-md-4">
            {/* Page Header */}
            <div className="text-center mb-4">
                <h1 className="stylish-heading mb-2" style={{ color: '#4169e1', fontWeight: 700 }}>
                    Website Templates
                </h1>
                <p className="text-muted small mb-0" style={{ maxWidth: '650px', margin: '0 auto', fontSize: '0.95rem' }}>
                    Chagua muundo bora wa tovuti (custom website template) wa shughuli yako.
                </p>


            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                    <p className="mt-3 text-muted fw-semibold">Inapakia website templates...</p>
                </div>
            ) : error ? (
                <Alert variant="danger" className="text-center my-4 rounded-3 shadow-sm">
                    {error}
                    <div className="mt-2">
                        <Button variant="outline-danger" size="sm" onClick={fetchCustomWebsites}>
                            Jaribu Tena (Retry)
                        </Button>
                    </div>
                </Alert>
            ) : templates.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-4 border">
                    <h5 className="fw-bold text-dark">Hakuna Custom Website Templates Zilizopatikana</h5>
                    <Button variant="outline-primary" size="sm" className="rounded-pill px-4" onClick={fetchCustomWebsites}>
                        Refresh List
                    </Button>
                </div>
            ) : (
                <Row className="g-4">
                    {templates.map((template) => {
                        const isActive = String(template.id) === String(activeWebsiteId) || template.is_selected;
                        const isSubmitting = savingTheme && selectedTemplateForModal?.id === template.id;

                        return (
                            <Col lg={4} md={6} xs={12} key={template.id}>
                                <Card className={`template-card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative ${isActive ? 'active-template' : ''}`}>
                                    {/* Badges Overlay - only shown for is_selected: true */}
                                    {isActive && (
                                        <div className="position-absolute top-0 end-0 m-3" style={{ zIndex: 2 }}>
                                            <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm fw-bold d-flex align-items-center gap-1">
                                                <FaCheckCircle size={12} /> Active Website
                                            </Badge>
                                        </div>
                                    )}

                                    {/* Preview Image */}
                                    {template.image ? (
                                        <div className="template-image-wrapper overflow-hidden bg-light position-relative">
                                            <Card.Img
                                                variant="top"
                                                src={template.image}
                                                alt={`${template.name} preview`}
                                                className="template-image"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://via.placeholder.com/480x280?text=Preview+Not+Available';
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="template-image-wrapper overflow-hidden bg-light position-relative d-flex align-items-center justify-content-center text-muted">
                                            <FaGlobe size={44} className="opacity-40 text-primary" />
                                        </div>
                                    )}

                                    <Card.Body className="d-flex flex-column p-4">
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="text-muted small fw-medium">
                                                    <FaPalette className="me-1 text-primary" /> {template.category}
                                                </span>

                                                {template.preview_url && (
                                                    <a
                                                        href={template.preview_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary text-decoration-none small d-inline-flex align-items-center gap-1"
                                                    >
                                                        <FaExternalLinkAlt size={11} /> Live Demo
                                                    </a>
                                                )}
                                            </div>

                                            <Card.Title className="template-name text-dark fw-bold mb-2 fs-5">
                                                {template.name}
                                            </Card.Title>

                                        </div>

                                        <div className="mt-auto">
                                            <Button
                                                className="w-100 rounded-pill py-2.5 fw-semibold border-0 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                                onClick={() => handleOpenSelectModal(template)}
                                                disabled={isSubmitting || isActive}
                                                style={{ backgroundColor: isActive ? '#28a745' : '#4169e1' }}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <FaSpinner className="fa-spin" size={14} /> Inahifadhi...
                                                    </>
                                                ) : isActive ? (
                                                    <>
                                                        <FaCheck size={14} /> Customized Website Active
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaMagic size={14} /> Chagua Website Template
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Modal for Selecting Website Template & Specifying Theme Colors */}
            <Modal
                show={showThemeModal}
                onHide={() => !savingTheme && setShowThemeModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton={!savingTheme} className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5 d-flex align-items-center gap-2" style={{ color: '#1e293b' }}>
                        <FaPalette className="text-primary" /> Chagua Rangi za shughuli
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="text-muted small mb-3">
                        Unachagua: <strong className="text-primary">{selectedTemplateForModal?.name}</strong>
                    </p>

                    <Form onSubmit={handleSaveThemeSettings}>
                        {/* Primary Color */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold small mb-1">Rangi Kuu (Primary Color) *</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    value={colorSettings.primary_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                    style={{ width: '48px', height: '40px', padding: '3px', cursor: 'pointer', borderRadius: '8px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={colorSettings.primary_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                    placeholder="#4169e1"
                                    className="font-monospace"
                                    required
                                />
                            </div>
                        </Form.Group>

                        {/* Secondary Color */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold small mb-1">Rangi ya Pili (Secondary Color) *</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    value={colorSettings.secondary_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                    style={{ width: '48px', height: '40px', padding: '3px', cursor: 'pointer', borderRadius: '8px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={colorSettings.secondary_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                    placeholder="#1e293b"
                                    className="font-monospace"
                                    required
                                />
                            </div>
                        </Form.Group>

                        {/* Tertiary Color (backend requires tertially_color) */}
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold small mb-1">Rangi ya Tatu (Tertiary Color) *</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    value={colorSettings.tertially_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, tertially_color: e.target.value }))}
                                    style={{ width: '48px', height: '40px', padding: '3px', cursor: 'pointer', borderRadius: '8px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={colorSettings.tertially_color}
                                    onChange={(e) => setColorSettings(prev => ({ ...prev, tertially_color: e.target.value }))}
                                    placeholder="#f8fafc"
                                    className="font-monospace"
                                    required
                                />
                            </div>
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                            <Button
                                variant="outline-secondary"
                                className="rounded-pill px-4"
                                onClick={() => setShowThemeModal(false)}
                                disabled={savingTheme}
                            >
                                Ghairi
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                className="rounded-pill px-4 d-flex align-items-center gap-2 fw-semibold"
                                disabled={savingTheme}
                                style={{ backgroundColor: '#4169e1', borderColor: '#4169e1' }}
                            >
                                {savingTheme ? (
                                    <>
                                        <FaSpinner className="fa-spin" size={14} /> Inahifadhi...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck size={14} /> Thibitisha na Hifadhi
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default WebsiteTemplates;